import React, {useState} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import {Modal} from '@forecast-it/design-system';
import {BasicNumberInput, LockIcon} from '@/web-components';

import {currencies, FEATURE_FLAGS, SOCKET_ACTION} from '../../../constants';
import Util from '../../shared/util/util';
import UpdateExchangeRateMutation from '../../../mutations/update_exchange_rate_mutation';
import ActionsMenu from '../../shared/components/action-menu/actions_menu';
import {createToast} from '../../shared/components/toasts/another-toast/toaster';
import DeleteExchangeRateMutation from '../../../mutations/delete_exchange_rate_mutation';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import {ConfirmationModalPrimaryButton} from '../../shared/components/modals/general/ConfirmationModal';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import {CSS_CONSTANTS} from '../../../css_variables';
import TooltipContainer from '../../shared/components/tooltips/tooltip_container';

export const CurrencyRow = injectIntl(({intl: {formatMessage}, exchangeRate, company, isFirstRow}) => {
	const currency = currencies.find(c => c.code === exchangeRate.currency);
	const [hasError, setHasError] = useState(false);

	const updateRate = rate => {
		if (rate <= 0) {
			setHasError(true);
		} else {
			setHasError(false);
			if (rate !== exchangeRate.rate) {
				Util.CommitMutation(
					UpdateExchangeRateMutation,
					{
						id: exchangeRate.id,
						rate: +rate,
					},
					null,
					true
				);
			}
		}
	};

	const deleteExchangeRate = () => {
		const onSuccess = () => {
			createToast({duration: 5000, message: formatMessage({id: 'exchange-rate.has-been-deleted'})});
		};
		const callbackPositive = () => {
			const useCurrencyPath = hasFeatureFlag(FEATURE_FLAGS.DATE_EFFECTIVE_CURRENCY_EXCHANGE_RATES);
			Util.CommitMutation(
				DeleteExchangeRateMutation,
				{
					companyId: company.id,
					id: exchangeRate.id,
					...(useCurrencyPath ? {currency: exchangeRate.currency} : {}),
				},
				onSuccess,
				true
			);
		};

		if (hasFeatureFlag(FEATURE_FLAGS.DATE_EFFECTIVE_CURRENCY_EXCHANGE_RATES)) {
			const confirmText = SOCKET_ACTION.DELETE;
			showModal({
				type: MODAL_TYPE.CONFIRMATION,
				title: formatMessage({id: 'settings_finance.delete_exchange_rate_modal_title'}),
				description: (
					<div style={{whiteSpace: 'pre-line'}}>
						<FormattedMessage
							id="settings_finance.delete_exchange_rate_modal_description"
							values={{
								currencyLabel: `${currency.name} ${currency.code}`,
								confirmText,
								b: chunks => <b>{chunks}</b>,
							}}
						/>
					</div>
				),
				modalSize: Modal.SIZE.MEDIUM,
				confirmPhrase: confirmText,
				includeDefaultMessage: false,
				confirmationModalPrimaryButton: ConfirmationModalPrimaryButton.DELETE,
				callbackPositive,
			});
		} else {
			showModal({
				type: MODAL_TYPE.GENERIC_DELETION_WARNING,
				deleteCallback: callbackPositive,
			});
		}
	};

	const canDelete = !exchangeRate.isUsed;

	return (
		<>
			<td className="column-currency">
				<div className="currency-wrapper">
					<div>{currency ? `${currency.name} - ${currency.code} (${currency.symbol})` : exchangeRate.currency}</div>
					<div className="currency-lock">
						{!canDelete && (
							<TooltipContainer infoText={formatMessage({id: 'settings_finance.currency_locked'})}>
								<LockIcon size={LockIcon.SIZE.STANDARD} color={CSS_CONSTANTS.v2_text_gray} />
							</TooltipContainer>
						)}
					</div>
				</div>
			</td>
			<td className="column-rate">
				<div className="rate-wrapper">
					<div className="rate-text">{`${company.currency} 1.00 = ${exchangeRate.currency}`}</div>
					<div className="rate-input" data-userpilot={isFirstRow ? 'currencies-rate' : null}>
						<BasicNumberInput value={exchangeRate.rate} callback={updateRate} hasError={hasError} width="70px" />
					</div>
				</div>
			</td>
			<td className="column-actions">
				{canDelete && (
					<ActionsMenu
						whiteInner={true}
						options={[
							{
								text: formatMessage({id: 'common.delete'}),
								onClick: deleteExchangeRate,
							},
						]}
					/>
				)}
			</td>
		</>
	);
});
